import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { prescriptionsApi } from '../../services/prescriptionsService'
import type { 
  Prescription, 
  CreatePrescriptionRequest, 
  UpdatePrescriptionRequest,
  PrescriptionsQueryParams 
} from '../../types/api'

// Async thunks
export const fetchPrescriptions = createAsyncThunk(
  'prescriptions/fetchPrescriptions',
  async (params?: PrescriptionsQueryParams) => {
    const response = await prescriptionsApi.getPrescriptions(params)
    return response
  }
)

export const fetchPrescription = createAsyncThunk(
  'prescriptions/fetchPrescription',
  async (prescriptionId: string) => {
    const response = await prescriptionsApi.getPrescription(prescriptionId)
    return response
  }
)

export const createPrescription = createAsyncThunk(
  'prescriptions/createPrescription',
  async (prescriptionData: CreatePrescriptionRequest) => {
    const response = await prescriptionsApi.createPrescription(prescriptionData)
    return response
  }
)

export const updatePrescription = createAsyncThunk(
  'prescriptions/updatePrescription',
  async ({ prescriptionId, prescriptionData }: { prescriptionId: string; prescriptionData: UpdatePrescriptionRequest }) => {
    const response = await prescriptionsApi.updatePrescription(prescriptionId, prescriptionData)
    return response
  }
)

// State interface
interface PrescriptionsState {
  prescriptions: Prescription[]
  currentPrescription: Prescription | null
  total: number
  loading: boolean
  error: string | null
  filters: {
    patientId: string | null
    status: 'pending' | 'filled' | 'cancelled' | null
  }
}

// Initial state
const initialState: PrescriptionsState = {
  prescriptions: [],
  currentPrescription: null,
  total: 0,
  loading: false,
  error: null,
  filters: {
    patientId: null,
    status: null,
  },
}

// Slice
const prescriptionsSlice = createSlice({
  name: 'prescriptions',
  initialState,
  reducers: {
    setPatientFilter: (state, action) => {
      state.filters.patientId = action.payload
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload
    },
    clearFilters: (state) => {
      state.filters = { patientId: null, status: null }
    },
    clearError: (state) => {
      state.error = null
    },
    clearCurrentPrescription: (state) => {
      state.currentPrescription = null
    },
  },
  extraReducers: (builder) => {
    // Fetch prescriptions
    builder
      .addCase(fetchPrescriptions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPrescriptions.fulfilled, (state, action) => {
        state.loading = false
        state.prescriptions = action.payload.prescriptions
        state.total = action.payload.total
      })
      .addCase(fetchPrescriptions.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch prescriptions'
      })

    // Fetch single prescription
    builder
      .addCase(fetchPrescription.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPrescription.fulfilled, (state, action) => {
        state.loading = false
        state.currentPrescription = action.payload
      })
      .addCase(fetchPrescription.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch prescription'
      })

    // Create prescription
    builder
      .addCase(createPrescription.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createPrescription.fulfilled, (state, action) => {
        state.loading = false
        state.prescriptions.push(action.payload)
        state.total += 1
      })
      .addCase(createPrescription.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create prescription'
      })

    // Update prescription
    builder
      .addCase(updatePrescription.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePrescription.fulfilled, (state, action) => {
        state.loading = false
        const index = state.prescriptions.findIndex(p => p.id === action.payload.id)
        if (index !== -1) {
          state.prescriptions[index] = action.payload
        }
        if (state.currentPrescription?.id === action.payload.id) {
          state.currentPrescription = action.payload
        }
      })
      .addCase(updatePrescription.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update prescription'
      })
  },
})

// Export actions
export const { setPatientFilter, setStatusFilter, clearFilters, clearError, clearCurrentPrescription } = prescriptionsSlice.actions

// Selectors
export const selectPrescriptions = (state: { prescriptions: PrescriptionsState }) => state.prescriptions.prescriptions
export const selectCurrentPrescription = (state: { prescriptions: PrescriptionsState }) => state.prescriptions.currentPrescription
export const selectPrescriptionsTotal = (state: { prescriptions: PrescriptionsState }) => state.prescriptions.total
export const selectPrescriptionsLoading = (state: { prescriptions: PrescriptionsState }) => state.prescriptions.loading
export const selectPrescriptionsError = (state: { prescriptions: PrescriptionsState }) => state.prescriptions.error
export const selectPrescriptionsFilters = (state: { prescriptions: PrescriptionsState }) => state.prescriptions.filters

export default prescriptionsSlice.reducer